import { Mail, MapPin, Building2, UserRound, Globe2, BadgeCheck, Award } from "lucide-react";
import { Container, Reveal, Eyebrow, LeafBackdrop } from "@/components/common";
import { PageHero } from "@/components/ui-extras";
import { COMPANY } from "@/data/content";

const TEAM = [
  { name: "Mr Sib Sankar Maitra", role: "Managing Director", icon: UserRound },
  { name: "Mr Rajanya Maitra", role: "Global Head", icon: Globe2 },
];

// Light teal/mint leadership card — icon block on top, uppercase role, bold name
const LeadershipCard = ({ name, role, icon: IconCmp }) => (
  <div
    data-testid="leadership-card"
    className="group flex h-full flex-col rounded-[var(--radius)] p-7 sm:p-8"
    style={{
      background: "#FFFFFF",
      border: "1px solid #B8EFE3",
      boxShadow: "0 10px 30px rgba(58,187,176,0.14)",
      transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-6px)";
      e.currentTarget.style.borderColor = "#3ABBB0";
      e.currentTarget.style.boxShadow = "0 0 0 1px rgba(58,187,176,0.4), 0 18px 44px rgba(58,187,176,0.28)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.borderColor = "#B8EFE3";
      e.currentTarget.style.boxShadow = "0 10px 30px rgba(58,187,176,0.14)";
    }}
  >
    <span
      className="flex h-16 w-16 items-center justify-center rounded-2xl"
      style={{ background: "linear-gradient(140deg,#3ABBB0,#41E19A)", boxShadow: "0 8px 20px rgba(58,187,176,0.35)" }}
    >
      <IconCmp size={28} color="#FFFFFF" />
    </span>
    <span className="mt-7 block font-sans text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "#3ABBB0" }}>
      {role}
    </span>
    <h3 className="mt-2 font-serif text-2xl font-semibold leading-tight" style={{ color: "#063B36" }}>
      {name}
    </h3>
    <div className="mt-auto pt-6">
      <div className="h-px w-full" style={{ background: "#DDFCF5" }} />
      <p className="mt-4 font-sans text-sm font-medium" style={{ color: "#36B4B2" }}>Prince Trading Impex</p>
    </div>
  </div>
);

const CompanyCard = () => (
  <div
    data-testid="contact-company-card"
    className="flex h-full flex-col rounded-[var(--radius)] p-7 sm:p-8"
    style={{
      background: "linear-gradient(150deg,#41E19A 0%,#3ABBB0 100%)",
      border: "1px solid #3ABBB0",
      boxShadow: "0 18px 44px rgba(58,187,176,0.35)",
    }}
  >
    <span
      className="flex h-16 w-16 items-center justify-center rounded-2xl"
      style={{ background: "rgba(255,255,255,0.85)", boxShadow: "0 8px 20px rgba(6,59,54,0.18)" }}
    >
      <Building2 size={28} color="#063B36" />
    </span>
    <span className="mt-7 block font-sans text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "#063B36" }}>
      Export House
    </span>
    <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-bold leading-tight" style={{ color: "#063B36" }}>
      Prince Trading Impex
    </h3>
    <p className="mt-1.5 font-sans text-sm font-semibold" style={{ color: "#063B36", opacity: 0.85 }}>
      India-Based Agricultural Export House
    </p>

    <div className="my-6 h-px w-full" style={{ background: "rgba(6,59,54,0.25)" }} />

    <div className="mt-auto space-y-4">
      <a
        href={`mailto:${COMPANY.email}`}
        data-testid="contact-email-card"
        className="group flex items-center gap-3.5 rounded-xl p-3 -m-3 transition-colors"
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.25)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full" style={{ background: "rgba(255,255,255,0.85)" }}>
          <Mail size={19} color="#063B36" />
        </span>
        <span className="min-w-0">
          <span className="block font-sans text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "#063B36", opacity: 0.8 }}>Email</span>
          <span className="block break-all font-sans text-sm sm:text-base font-semibold" style={{ color: "#063B36" }}>{COMPANY.email}</span>
        </span>
      </a>

      <div className="flex items-center gap-3.5">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full" style={{ background: "rgba(255,255,255,0.85)" }}>
          <MapPin size={19} color="#063B36" />
        </span>
        <span>
          <span className="block font-sans text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "#063B36", opacity: 0.8 }}>Location</span>
          <span className="block font-sans text-base font-semibold" style={{ color: "#063B36" }}>West Bengal, India</span>
        </span>
      </div>
    </div>
  </div>
);

const Contact = () => {
  return (
    <div>
      <PageHero
        eyebrow="Contact"
        title="Speak with Prince Trading Impex"
        intro="For commodity requirements, product specifications, packaging, or private label export — reach our team directly by email. We respond to serious global buyers."
      />

      <section className="relative overflow-hidden py-16 sm:py-20 lg:py-24" style={{ background: "#E8FFF6" }}>
        {/* Subtle logo-inspired leaf/wreath watermarks */}
        <LeafBackdrop size={520} className="-right-28 -top-24 opacity-40" color="rgba(58,187,176,0.10)" />
        <LeafBackdrop size={360} className="-left-24 bottom-10 opacity-30" color="rgba(65,225,154,0.10)" />

        <Container className="relative">
          {/* ===== Key Company Contacts ===== */}
          <Reveal>
            <div className="flex flex-col items-center text-center">
              <Eyebrow>Team</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-[2.6rem] font-semibold leading-tight" style={{ color: "#063B36" }}>
                Key Company Contacts
              </h2>
              <p className="mt-4 max-w-2xl font-sans text-base leading-relaxed" style={{ color: "#2f6b62" }}>
                Connect directly with the leadership driving Prince Trading Impex's sourcing and global trade execution.
              </p>
            </div>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <LeadershipCard name={m.name} role={m.role} icon={m.icon} />
              </Reveal>
            ))}
            <Reveal delay={0.16}>
              <CompanyCard />
            </Reveal>
          </div>

          {/* Certification / credibility badges */}
          <Reveal delay={0.1}>
            <div data-testid="contact-cert-badges" className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full py-2 pl-2 pr-4 font-sans text-xs sm:text-[13px] font-semibold" style={{ background: "#FFFFFF", border: "1px solid #B8EFE3", color: "#063B36" }}>
                <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ background: "linear-gradient(140deg,#3ABBB0,#41E19A)" }}>
                  <BadgeCheck size={15} color="#FFFFFF" />
                </span>
                ISO 22000:2018 Certified
              </span>
              <span className="inline-flex items-center gap-2 rounded-full py-2 pl-2 pr-4 font-sans text-xs sm:text-[13px] font-semibold" style={{ background: "#FFFFFF", border: "1px solid #B8EFE3", color: "#063B36" }}>
                <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ background: "linear-gradient(140deg,#3ABBB0,#41E19A)" }}>
                  <Award size={15} color="#FFFFFF" />
                </span>
                Government of India Recognized Star Export House
              </span>
            </div>
          </Reveal>

          {/* ===== Email Us (no form — email-based contact only) ===== */}
          <Reveal delay={0.1}>
            <div
              data-testid="contact-email-section"
              className="mt-16 lg:mt-24 overflow-hidden rounded-[var(--radius)] p-8 sm:p-12 text-center"
              style={{ background: "#FFFFFF", border: "1px solid #B8EFE3", boxShadow: "0 14px 40px rgba(58,187,176,0.16)" }}
            >
              <div className="mx-auto flex max-w-2xl flex-col items-center">
                <span
                  className="flex h-16 w-16 items-center justify-center rounded-2xl"
                  style={{ background: "linear-gradient(140deg,#3ABBB0,#41E19A)", boxShadow: "0 8px 20px rgba(58,187,176,0.35)" }}
                >
                  <Mail size={28} color="#FFFFFF" />
                </span>
                <div className="mt-6"><Eyebrow>Get in Touch</Eyebrow></div>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold leading-tight" style={{ color: "#063B36" }}>
                  Discuss Your Supply Requirement
                </h2>
                <p className="mt-5 font-sans text-base leading-relaxed" style={{ color: "#2f6b62" }}>
                  Email us with your product interest, required quantity, destination market, and preferred trade terms, and our team will respond with relevant specifications and pricing.
                </p>

                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="contact-email-button"
                    className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-sans text-sm font-semibold transition-all"
                    style={{ background: "linear-gradient(90deg,#3ABBB0,#41E19A)", color: "#020606", boxShadow: "0 10px 26px rgba(58,187,176,0.4)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
                    onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
                  >
                    <Mail size={16} /> Discuss Supply Requirement
                  </a>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="contact-email-link"
                    className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-sans text-sm font-semibold transition-colors"
                    style={{ background: "#FFFFFF", color: "#063B36", border: "1px solid #B8EFE3" }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#3ABBB0")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#B8EFE3")}
                  >
                    <Mail size={16} color="#3ABBB0" /> {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
};

export default Contact;
